import React, { useCallback, useMemo } from 'react';
import { shallowEqual } from 'react-redux';

import Typography from '@mui/material/Typography';

import type { NavSectionConfig } from 'containers/App/paths';
import { getNavFromKey, toNavSectionItem } from 'containers/App/paths';
import { useAppSelector } from 'hooks/redux';

import { MainNavItem } from '../NavItems';
import { useNavExpansion } from '../NavExpansionContext';
import { useNavBarStyle } from '../commonNavStyles';
import { isNavItemVisible } from '../navVisibility';

const NavSectionHeader = React.memo(function NavSectionHeader({ id, title }: { id: string; title: string }) {
  const { classes, cx } = useNavBarStyle();
  const { canExpand, expanded, toggle } = useNavExpansion();
  const canExpandFromHeader = canExpand && !expanded;
  const handleClick = useCallback(() => toggle('section-header'), [toggle]);

  return (
    <div
      className={cx(classes.sectionHeader, canExpandFromHeader && classes.sectionHeaderExpandable)}
      onClick={canExpandFromHeader ? handleClick : undefined}
    >
      <Typography id={id} variant='captionBold' noWrap className={classes.sectionTitle}>
        {title}
      </Typography>
      <div className={classes.sectionDivider} aria-hidden />
    </div>
  );
});

export const NavSection = React.memo(function NavSection({
  section,
}: {
  section: NavSectionConfig;
}): React.JSX.Element | null {
  const { classes } = useNavBarStyle();
  const entries = useMemo(
    () =>
      section.items.flatMap((item) => {
        const { key, ...overrides } = toNavSectionItem(item);
        const nav = getNavFromKey(key);
        return nav ? [{ nav, ...overrides }] : [];
      }),
    [section],
  );

  const itemAccess = useAppSelector(
    (state) => entries.map(({ accessSelector }) => (accessSelector ? accessSelector(state) : true)),
    shallowEqual,
  );
  const accessibleEntries = entries.filter((_, index) => itemAccess[index]);
  const showSection = useAppSelector((state) => accessibleEntries.some(({ nav }) => isNavItemVisible(state, nav)));

  if (!showSection) {
    return null;
  }

  const items = accessibleEntries.map(({ nav, label, icon }) => (
    <MainNavItem key={`nav-item-${nav.path}`} nav={nav} label={label} icon={icon} section='main' />
  ));

  if (!section.title) {
    return <div className={classes.untitledSection}>{items}</div>;
  }

  const titleId = `nav-section-${section.id}`;
  return (
    <div role='group' aria-labelledby={titleId}>
      <NavSectionHeader id={titleId} title={section.title} />
      {items}
    </div>
  );
});
